import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LANGUAGE, isSupportedPrefix } from '../config/languages.js';

// Splits "/path?query#hash" into ["/path", "?query#hash"].
function splitPathFromQueryAndHash(path) {
  const match = path.match(/^([^?#]*)([?#].*)?$/);
  return [match[1] || '/', match[2] || ''];
}

// Given a pathname (with or without a language prefix), returns the
// active language code and the unprefixed "rest" of the path.
export function stripLangPrefix(pathname) {
  const segments = pathname.split('/');
  const maybeLang = segments[1];

  if (maybeLang && isSupportedPrefix(maybeLang)) {
    const rest = `/${segments.slice(2).join('/')}`;
    return { lang: maybeLang, rest };
  }

  return { lang: DEFAULT_LANGUAGE, rest: pathname || '/' };
}

// Given an unprefixed (or already-prefixed) app path and a target
// language code, returns the correctly prefixed path. The default
// language never gets a prefix. Query strings/hashes are preserved.
export function buildLocalizedPath(path, langCode) {
  const [pathname, queryAndHash] = splitPathFromQueryAndHash(path);
  const { rest } = stripLangPrefix(pathname);
  // No trailing slashes anywhere except the bare root (matches the
  // CloudFront Function's 301s, see infra/cloudfront/).
  const normalizedRest = rest.replace(/\/+$/, '');
  const prefix = !langCode || langCode === DEFAULT_LANGUAGE ? '' : `/${langCode}`;
  const finalPath = `${prefix}${normalizedRest}`;
  return (finalPath || '/') + queryAndHash;
}

// Current language + a helper to localize an app path to it.
export function useLocalizedPath() {
  const location = useLocation();
  const { lang } = stripLangPrefix(location.pathname);
  const localize = (path) => buildLocalizedPath(path, lang);
  return { lang, localize };
}

// Drop-in replacement for react-router's useNavigate() that keeps the
// current language prefix on every navigation.
export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const { lang } = useLocalizedPath();
  return (to, options) => {
    if (typeof to === 'number') {
      navigate(to);
      return;
    }
    navigate(buildLocalizedPath(to, lang), options);
  };
}
